import React, { useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import InputCalculator from "../common/InputCalculator";
import { normalizeCalculatorValue, hasUnevaluatedExpression } from "../common/calculatorValue";
import BatchPagination from "../batch/BatchPagination";
import LoaderSpinner from "../common/Loader";
import { numOfPage } from "../../constant";
import {
    getDropshipAgreements,
    updateDropshipAgreement,
    getDropshipMarginConfig,
} from "../../store/actions/dropship.action";
import ViewImage from "../common/ViewImage";
import "../DropshipExplore/dropshipExplore.css";

const STATUS_BADGE_CLASS = {
    Active: "bg-success",
    Cancelled: "bg-danger",
    Expired: "bg-secondary",
};

const getStatusBadgeClass = (status) => STATUS_BADGE_CLASS[status] || "bg-warning";

const getExpiryColor = (daysUntilExpiry) => {
    if (daysUntilExpiry <= 7) return "#dc3545";
    if (daysUntilExpiry <= 30) return "#ffc107";
    return "#28a745";
};

function AgreementRow({
    item,
    rowNumber,
    isEditing,
    editPrice,
    setEditPrice,
    marginFlat,
    marginPercent,
    onEdit,
    onSave,
    onCancelEdit,
    onCancelClick,
    onThumbnailClick,
}) {
    const sellingForCalc = isEditing
        ? Number.parseFloat(editPrice) || 0
        : item.sellingPrice;
    const supplierWholesale = item.wholesalePriceAtAgreement || 0;
    // Bake the 3PL platform margin into the wholesale shown to the dropshipper.
    // They see one number ("Wholesale") and a margin computed off it.
    const effectiveWholesale = supplierWholesale + marginFlat + (supplierWholesale * marginPercent) / 100;
    const currentMargin = sellingForCalc - effectiveWholesale;

    return (
        <tr>
            <td>{rowNumber}</td>
            <td>
                {item.imageUrl ? (
                    <button
                        type="button"
                        className="thumbImg sortBtn"
                        onClick={() => onThumbnailClick?.(item)}
                        title="Click to view larger"
                    >
                        <img
                            src={item.imageUrl}
                            alt={item.productName}
                            className="dropship-product-thumb"
                        />
                    </button>
                ) : (
                    <div className="dropship-no-image">
                        <i className="fa fa-image" />
                    </div>
                )}
            </td>
            <td>
                <div style={{ fontSize: "12px", color: "#666" }}>{item.originalSKU}</div>
                <div style={{ fontSize: "11px", color: "#999" }}>{item.importedSKU}</div>
            </td>
            <td>{item.productName}</td>
            <td>Verified Supplier</td>
            <td>${effectiveWholesale.toFixed(2)}</td>
            <td>
                {isEditing ? (
                    <InputCalculator
                        inputMode="decimal"
                        maxLength={50}
                        value={editPrice}
                        onChange={(e) => {
                            const value = normalizeCalculatorValue(e);
                            if (value !== null) setEditPrice(value);
                        }}
                        portal
                        autoFocus
                    />
                ) : (
                    <span>${item.sellingPrice?.toFixed(2)}</span>
                )}
            </td>
            <td>
                <span style={{ color: currentMargin >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                    ${currentMargin?.toFixed(2)}
                </span>
            </td>
            <td>
                <div style={{ fontSize: "12px" }}>
                    <span style={{ fontWeight: 600 }}>{item.soldQuantity || 0}</span>
                    {" / "}
                    <span>{item.reservedQuantity || 0}</span>
                </div>
                <div style={{ fontSize: "11px", color: "#666" }}>
                    {item.remainingQuantity || 0} left
                </div>
            </td>
            <td style={{ fontSize: "12px" }}>
                {item.status === "Active" ? (
                    <span style={{ color: getExpiryColor(item.daysUntilExpiry), fontWeight: 600 }}>
                        {item.daysUntilExpiry > 0 ? `${item.daysUntilExpiry} days` : "Expired"}
                    </span>
                ) : (
                    <span style={{ color: "#999" }}>—</span>
                )}
            </td>
            <td>
                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                    {item.status}
                </span>
            </td>
            <td>
                {item.status === "Active" && (
                    <div className="d-flex gap-1">
                        {isEditing ? (
                            <>
                                <Button
                                    className="cus-seconday-bg-btn btn-sm"
                                    onClick={() => onSave(item.id)}
                                >
                                    Save
                                </Button>
                                <Button
                                    className="cus-primary-transparent-btn btn-sm"
                                    onClick={onCancelEdit}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    className="cus-primary-transparent-btn btn-sm"
                                    onClick={() => onEdit(item)}
                                >
                                    <i className="fa fa-pencil" /> Edit Price
                                </Button>
                                <Button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => onCancelClick(item.id)}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}

AgreementRow.propTypes = {
    item: PropTypes.object.isRequired,
    rowNumber: PropTypes.number.isRequired,
    isEditing: PropTypes.bool.isRequired,
    editPrice: PropTypes.string.isRequired,
    setEditPrice: PropTypes.func.isRequired,
    marginFlat: PropTypes.number.isRequired,
    marginPercent: PropTypes.number.isRequired,
    onEdit: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    onCancelClick: PropTypes.func.isRequired,
    onThumbnailClick: PropTypes.func,
};

AgreementRow.defaultProps = {
    onThumbnailClick: undefined,
};

export default function DropshipAgreements() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { agreementsList, agreementsListStatus, agreementsTotalRecords, marginConfigs } = useSelector(
        (state) => state.dropshipReducer
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [editingId, setEditingId] = useState(null);
    const [editPrice, setEditPrice] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelTargetId, setCancelTargetId] = useState(null);
    const [showHideImageModal, setShowHideImageModal] = useState(false);
    const [imageRecord, setImageRecord] = useState({ imageUrl: "" });

    const handleThumbnailClick = (item) => {
        setImageRecord(item);
        setShowHideImageModal(true);
    };

    const loader = agreementsListStatus === "PENDING";

    useEffect(() => {
        loadAgreements();
        dispatch(getDropshipMarginConfig({}));
    }, [currentPage, pageSize]);

    const globalMargin = marginConfigs.find((c) => c.scope === "Global") || { flatAmount: 0, percentAmount: 0 };
    const marginFlat = Number.parseFloat(globalMargin.flatAmount) || 0;
    const marginPercent = Number.parseFloat(globalMargin.percentAmount) || 0;

    const loadAgreements = () => {
        dispatch(
            getDropshipAgreements({
                pageIndex: currentPage,
                pageSize: pageSize,
                role: "dropshipper",
            })
        );
    };

    const handleEditPrice = (agreement) => {
        setEditingId(agreement.id);
        setEditPrice(agreement.sellingPrice?.toString() || "");
    };

    const handleSavePrice = async (agreementId) => {
        const newPrice = Number.parseFloat(editPrice);
        if (!newPrice || newPrice <= 0 || hasUnevaluatedExpression(editPrice)) {
            toast.error("Please enter a valid selling price.");
            return;
        }
        try {
            const result = await updateDropshipAgreement({
                agreementId: agreementId,
                sellingPrice: newPrice,
            });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Selling price updated.");
                setEditingId(null);
                loadAgreements();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Update failed.");
            }
        } catch (err) {
            toast.error("Failed to update selling price.");
        }
    };

    const handleCancelClick = (agreementId) => {
        setCancelTargetId(agreementId);
        setShowCancelModal(true);
    };

    const handleConfirmCancel = async () => {
        try {
            const result = await updateDropshipAgreement({
                agreementId: cancelTargetId,
                status: "Cancelled",
            });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Agreement cancelled.");
                setShowCancelModal(false);
                setCancelTargetId(null);
                loadAgreements();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Cancel failed.");
            }
        } catch (err) {
            toast.error("Failed to cancel agreement.");
        }
    };

    const totalPages = Math.ceil(agreementsTotalRecords / pageSize) || 1;

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    My Imported Products
                    <div className="top_right_button_container">
                        <Button
                            className="cus-seconday-bg-btn"
                            onClick={() => navigate("/dropship/explore")}
                        >
                            Explore Products
                        </Button>
                    </div>
                </div>

                <div className="main_content_table incomingTbl">
                    {loader && (
                        <div className="full-screen-spinner-overlay">
                            <LoaderSpinner />
                        </div>
                    )}

                    <div className="tableFixHead">
                        <Table className="table table-borderless">
                            <thead>
                                <tr>
                                    <th width="4%">#</th>
                                    <th width="6%">Thumbnail</th>
                                    <th width="9%">SKU</th>
                                    <th width="11%">Product</th>
                                    <th width="9%">Supplier</th>
                                    <th width="7%">Wholesale</th>
                                    <th width="8%">Selling</th>
                                    <th width="7%">Margin</th>
                                    <th width="9%">Reserved / Sold</th>
                                    <th width="8%">Expires In</th>
                                    <th width="7%">Status</th>
                                    <th width="13%">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loader && agreementsList.length === 0 && (
                                    <tr>
                                        <td colSpan="12" className="text-center py-4" style={{ color: "#999" }}>
                                            No imported products yet. Browse the Explore page to import products.
                                        </td>
                                    </tr>
                                )}
                                {agreementsList.map((item, index) => (
                                    <AgreementRow
                                        key={item.id}
                                        item={item}
                                        rowNumber={(currentPage - 1) * pageSize + index + 1}
                                        isEditing={editingId === item.id}
                                        editPrice={editPrice}
                                        setEditPrice={setEditPrice}
                                        marginFlat={marginFlat}
                                        marginPercent={marginPercent}
                                        onEdit={handleEditPrice}
                                        onSave={handleSavePrice}
                                        onCancelEdit={() => setEditingId(null)}
                                        onCancelClick={handleCancelClick}
                                        onThumbnailClick={handleThumbnailClick}
                                    />
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {agreementsTotalRecords > 0 && (
                        <BatchPagination
                            gridData={agreementsList}
                            numberOfPage={pageSize}
                            numOfPage={numOfPage}
                            loader={loader}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={agreementsTotalRecords}
                            setCurrentPage={setCurrentPage}
                            setNumberOfPage={(val) => {
                                setPageSize(val);
                                setCurrentPage(1);
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Cancel Confirmation Modal */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Cancel Agreement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to cancel this import agreement? The imported product will remain in your
                    catalog but no new orders will be routed to the supplier.
                </Modal.Body>
                <Modal.Footer>
                    <Button className="cus-primary-transparent-btn" onClick={() => setShowCancelModal(false)}>
                        No, Keep It
                    </Button>
                    <Button className="btn btn-danger" onClick={handleConfirmCancel}>
                        Yes, Cancel Agreement
                    </Button>
                </Modal.Footer>
            </Modal>

            <ViewImage
                title="Product Image"
                selectedRecord={imageRecord}
                showHideImageModal={showHideImageModal}
                setShowHideImageModal={setShowHideImageModal}
            />
        </main>
    );
}
